import React, { useState, useMemo, useRef, useEffect } from 'react';
import './MobileSearch.css';

function MobileSearch({
  isOpen,
  onClose,
  donors = {},
  groups = [],
  savedLists = [],
  savedTables = [],
  feedEntries = [],
  onNavigate
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  // Search results
  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();

    // Search people by name and narrative
    const people = Object.entries(donors)
      .filter(([name, data]) =>
        name.toLowerCase().includes(q) ||
        data.narrative?.toLowerCase().includes(q)
      )
      .slice(0, 5)
      .map(([name, data]) => ({ name, ...data }));

    // Search groups by name
    const groupResults = groups
      .filter(g => g.name.toLowerCase().includes(q))
      .slice(0, 5);

    // Search lists by name
    const listResults = savedLists
      .filter(l => l.name?.toLowerCase().includes(q))
      .slice(0, 3);

    // Search tables by name
    const tableResults = savedTables
      .filter(t => t.name?.toLowerCase().includes(q))
      .slice(0, 3);

    // Search feed by message content
    const feedResults = feedEntries
      .filter(e => e.message?.toLowerCase().includes(q))
      .slice(0, 5);

    const hasResults = people.length > 0 || groupResults.length > 0 ||
      listResults.length > 0 || tableResults.length > 0 || feedResults.length > 0;

    return hasResults ? {
      people,
      groups: groupResults,
      lists: listResults,
      tables: tableResults,
      feed: feedResults
    } : null;
  }, [query, donors, groups, savedLists, savedTables, feedEntries]);

  // Format relative date
  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle result click
  const handleResultClick = (type, item) => {
    onNavigate({ type, ...item });
  };

  // Get avatar color
  const getAvatarColor = (name) => {
    const colors = ['#a78bfa', '#60a5fa', '#34d399', '#2dd4bf', '#818cf8', '#22d3d1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Get initials
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!isOpen) return null;

  return (
    <div className="mobile-search-overlay" onClick={onClose}>
      <div className="mobile-search" onClick={e => e.stopPropagation()}>
        {/* Search input */}
        <div className="search-input-container">
          <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Search people, groups, feed..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoFocus
          />
          {query && (
            <button className="search-clear" onClick={() => setQuery('')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Results */}
        <div className="search-results">
          {!query.trim() && (
            <div className="search-hint">
              Start typing to search across all your data
            </div>
          )}

          {query.trim() && !searchResults && (
            <div className="search-no-results">
              No results for "{query}"
            </div>
          )}

          {searchResults && (
            <>
              {/* People */}
              {searchResults.people.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">People</div>
                  {searchResults.people.map(person => (
                    <div
                      key={person.name}
                      className="search-result"
                      onClick={() => handleResultClick('person', { name: person.name })}
                    >
                      <div
                        className="result-avatar"
                        style={{ borderColor: getAvatarColor(person.name), color: getAvatarColor(person.name) }}
                      >
                        {getInitials(person.name)}
                      </div>
                      <div className="result-info">
                        <div className="result-name">{person.name}</div>
                        <div className="result-meta">
                          {person.amount && person.amount !== 'Unknown' && (
                            <span>{person.amount}</span>
                          )}
                          {person.lastContact && (
                            <span>{formatRelativeDate(person.lastContact)}</span>
                          )}
                        </div>
                      </div>
                      <span className="result-arrow">›</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Groups */}
              {searchResults.groups.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Groups</div>
                  {searchResults.groups.map(group => (
                    <div
                      key={group.id}
                      className="search-result"
                      onClick={() => handleResultClick('group', { id: group.id, name: group.name })}
                    >
                      <div
                        className="result-icon"
                        style={{ background: group.color || '#a78bfa' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </div>
                      <div className="result-info">
                        <div className="result-name">{group.name}</div>
                        <div className="result-meta">
                          {group.memberCount !== undefined && (
                            <span>{group.memberCount} member{group.memberCount !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </div>
                      <span className="result-arrow">›</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Lists */}
              {searchResults.lists.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Lists</div>
                  {searchResults.lists.map(list => (
                    <div
                      key={list.id}
                      className="search-result"
                      onClick={() => handleResultClick('list', { id: list.id, name: list.name, groupId: list.groupId })}
                    >
                      <div className="result-icon list-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                        </svg>
                      </div>
                      <div className="result-info">
                        <div className="result-name">{list.name}</div>
                        <div className="result-meta">
                          <span>List</span>
                        </div>
                      </div>
                      <span className="result-arrow">›</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Tables */}
              {searchResults.tables.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Tables</div>
                  {searchResults.tables.map(table => (
                    <div
                      key={table.id}
                      className="search-result"
                      onClick={() => handleResultClick('table', { id: table.id, name: table.name, groupId: table.groupId })}
                    >
                      <div className="result-icon table-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <path d="M3 9h18M9 21V9" />
                        </svg>
                      </div>
                      <div className="result-info">
                        <div className="result-name">{table.name}</div>
                        <div className="result-meta">
                          <span>Table</span>
                        </div>
                      </div>
                      <span className="result-arrow">›</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Feed */}
              {searchResults.feed.length > 0 && (
                <div className="search-section">
                  <div className="search-section-label">Feed</div>
                  {searchResults.feed.map(entry => (
                    <div
                      key={entry.id}
                      className="search-result"
                      onClick={() => handleResultClick('feed', { id: entry.id })}
                    >
                      <div className="result-icon feed-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <div className="result-info">
                        <div className="result-name result-message">
                          {entry.message?.slice(0, 60)}{entry.message?.length > 60 ? '...' : ''}
                        </div>
                        <div className="result-meta">
                          <span>{formatRelativeDate(entry.timestamp)}</span>
                          {entry.donorName && <span>• {entry.donorName}</span>}
                        </div>
                      </div>
                      <span className="result-arrow">›</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MobileSearch;
